import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Trash2, Wand2, ImagePlus, Type, Heading1, Lightbulb, Film, ListOrdered } from "lucide-react";
import { api, API } from "../api";

const TYPES = [
  { key: "heading", label: "عنوان", icon: Heading1 },
  { key: "text", label: "نص", icon: Type },
  { key: "image", label: "صورة", icon: ImagePlus },
  { key: "video", label: "فيديو", icon: Film },
  { key: "words", label: "كلمات", icon: ListOrdered },
  { key: "tip", label: "نصيحة", icon: Lightbulb },
];

const blank = (type) => ({
  id: Math.random().toString(36).slice(2, 10), type,
  text: "", image_id: "", video_id: "", words: [], align: "start",
});

export const imageUrl = (id) => `${API}/images/${id}/file`;

export const BlocksEditor = ({ topic, videos = [], onSave }) => {
  const [blocks, setBlocks] = useState(() => (topic.blocks || []).map((b) => ({ ...blank(b.type), ...b })));
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const targetRef = useRef(null);

  const patch = (id, next) => setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...next } : b)));
  const remove = (id) => setBlocks((bs) => bs.filter((b) => b.id !== id));
  const move = (i, dir) => setBlocks((bs) => {
    const j = i + dir;
    if (j < 0 || j >= bs.length) return bs;
    const copy = [...bs];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const add = (type) => setBlocks((bs) => [...bs, blank(type)]);

  const pickImage = (id) => { targetRef.current = id; fileRef.current.click(); };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/images", fd, { timeout: 180000 });
      patch(targetRef.current, { image_id: data.id });
      toast.success("تم رفع الصورة");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل رفع الصورة");
    } finally { setBusy(false); }
  };

  const aiText = async (b) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/block-text", {
        title: topic.title_en, grade: topic.grade, kind: b.type, hint: b.text || "",
      });
      if (b.type === "words") patch(b.id, { words: String(data.text).split(/[,\n]/).map((w) => w.trim()).filter(Boolean) });
      else patch(b.id, { text: data.text });
      toast.success("تم التوليد بالذكاء الاصطناعي");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل التوليد");
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/topics/${topic.id}`, { ...topic, blocks });
      toast.success("تم حفظ ترتيب الصفحة");
      onSave?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحفظ"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="blocks-editor">
      <input ref={fileRef} type="file" accept="image/*" className="hidden"
        onChange={uploadImage} data-testid="block-image-input" />

      <div className="flex flex-wrap gap-2">
        {TYPES.map(({ key, label, icon: Icon }) => (
          <button key={key} type="button" onClick={() => add(key)} data-testid={`add-block-${key}`}
            className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1">
            <Icon size={14} />{label}</button>
        ))}
      </div>

      {!blocks.length && (
        <p className="text-sm text-white/50" data-testid="blocks-empty">
          لا توجد عناصر بعد — أضف عنواناً أو نصاً أو صورة لتبني شكل صفحة الدرس.</p>
      )}

      {blocks.map((b, i) => (
        <div key={b.id} className="rounded-2xl border border-white/15 bg-white/5 p-4 space-y-3"
          data-testid={`block-${b.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold text-white/60">
              {i + 1}. {TYPES.find((t) => t.key === b.type)?.label || b.type}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} data-testid={`block-up-${b.id}`}
                className="p-2 rounded-lg bg-white/10 text-white"><ChevronUp size={14} /></button>
              <button type="button" onClick={() => move(i, 1)} data-testid={`block-down-${b.id}`}
                className="p-2 rounded-lg bg-white/10 text-white"><ChevronDown size={14} /></button>
              <button type="button" onClick={() => remove(b.id)} data-testid={`block-delete-${b.id}`}
                className="p-2 rounded-lg bg-red-500/20 text-red-300"><Trash2 size={14} /></button>
            </div>
          </div>

          {b.type === "image" ? (
            <div className="flex items-center gap-3 flex-wrap">
              {b.image_id
                ? <img src={imageUrl(b.image_id)} alt="" className="h-24 rounded-xl object-cover"
                    data-testid={`block-image-preview-${b.id}`} />
                : <div className="h-24 w-32 rounded-xl bg-white/10 grid place-items-center text-white/40 text-xs">
                    لا صورة</div>}
              <button type="button" onClick={() => pickImage(b.id)} disabled={busy}
                data-testid={`block-upload-${b.id}`}
                className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1">
                <ImagePlus size={14} />{b.image_id ? "تغيير الصورة" : "ارفع صورة"}</button>
              <input className="input flex-1 min-w-[160px]" placeholder="تعليق أسفل الصورة (اختياري)"
                value={b.text} onChange={(e) => patch(b.id, { text: e.target.value })}
                data-testid={`block-caption-${b.id}`} />
            </div>
          ) : b.type === "video" ? (
            <select className="input" value={b.video_id} data-testid={`block-video-${b.id}`}
              onChange={(e) => patch(b.id, { video_id: e.target.value })}>
              <option value="">— اختر فيديو مرفوع —</option>
              {videos.map((v) => <option key={v.id} value={v.id}>{v.original_filename}</option>)}
            </select>
          ) : b.type === "words" ? (
            <input className="input" dir="ltr" placeholder="كلمات مفصولة بفاصلة" value={(b.words || []).join(", ")}
              data-testid={`block-words-${b.id}`}
              onChange={(e) => patch(b.id, { words: e.target.value.split(",").map((w) => w.trim()).filter(Boolean) })} />
          ) : b.type === "heading" ? (
            <input className="input" dir="ltr" placeholder="العنوان" value={b.text} data-testid={`block-text-${b.id}`}
              onChange={(e) => patch(b.id, { text: e.target.value })} />
          ) : (
            <textarea className="input" dir={b.type === "tip" ? "rtl" : "ltr"} rows={b.type === "tip" ? 2 : 4} value={b.text}
              data-testid={`block-text-${b.id}`} placeholder={b.type === "tip" ? "نصيحة قصيرة" : "النص"}
              onChange={(e) => patch(b.id, { text: e.target.value })} />
          )}

          {b.type !== "image" && b.type !== "video" && (
            <button type="button" onClick={() => aiText(b)} disabled={busy}
              data-testid={`block-ai-${b.id}`}
              className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1">
              <Wand2 size={14} />{busy ? "..." : "توليد بالذكاء الاصطناعي"}</button>
          )}
        </div>
      ))}

      <button type="button" onClick={save} disabled={busy} data-testid="blocks-save-btn"
        className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
        {busy ? "..." : "حفظ شكل الصفحة"}</button>
    </div>
  );
};
